// Recurring Invoice — the maths and the API mapping, kept out of the screens.
//
// Nothing here fetches or holds state. The API itself is new: every endpoint
// and field is specified in docs/BACKEND-COMPLETE.md (Part 5.6).

import { calcBillTotals } from "../../../core/utils/salesTotals";

/* ------------------------------------------------------------ lists */

export const INVOICE_TYPES = ["AMC", "Hosting Charge", "Support Charge"];
export const REPEATS = ["Daily", "Weekly", "Monthly", "Quarterly", "Yearly"];
export const TERMS = [
  { k: "Advance", days: 0 },
  { k: "Within 10 Days", days: 10 },
  { k: "Within 15 Days", days: 15 },
  { k: "Within 30 Days", days: 30 },
];
export const STATUSES = ["Active", "Paused", "Completed", "Cancelled"];
export const PAY_STATUSES = ["Pending", "Paid"];
export const PAY_MODES = ["Bank transfer", "Cheque", "Cash", "Card"];

export const TYPE_CLS = { AMC: "amc", "Hosting Charge": "host", "Support Charge": "supp" };
export const TYPE_COL = { AMC: "var(--s1)", "Hosting Charge": "var(--s3)", "Support Charge": "var(--s2)" };

const UNIT = { Daily: "day", Weekly: "week", Monthly: "month", Quarterly: "quarter", Yearly: "year" };

/* ------------------------------------------------------------ numbers */

export const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
export const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
export const money = (n) =>
  (n < 0 ? "-" : "") + Math.abs(Math.round(num(n))).toLocaleString("en-US");
export const money2 = (n) =>
  (n < 0 ? "-" : "") +
  Math.abs(num(n)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const compact = (n) => {
  const a = Math.abs(num(n));
  if (a >= 1000000) return (n / 1000000).toFixed(a >= 10000000 ? 1 : 2).replace(/\.0+$/, "") + "M";
  if (a >= 100000) return Math.round(n / 1000).toLocaleString("en-US") + "K";
  return money(n);
};
export const pct = (n, d) => (d ? (n / d) * 100 : 0);
export const fmtPct = (n, dp = 1) => (n >= 0 ? "" : "-") + Math.abs(n).toFixed(dp) + "%";

/* ------------------------------------------------------------ dates */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const today = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
export const iso = (d) =>
  d
    ? d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0")
    : "";
// accepts "2026-10-06", "2026-10-06T00:00:00" or a Date
export const pISO = (s) => {
  if (!s) return null;
  if (s instanceof Date) return new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const p = String(s).slice(0, 10).split("-");
  if (p.length < 3 || !+p[0]) return null;
  return new Date(+p[0], +p[1] - 1, +p[2]);
};
export const isoOf = (s) => iso(pISO(s));
export const addDays = (d, n) => {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + n);
  return x;
};
// month arithmetic that keeps the day, clamped to the month's length (31 Jan + 1 → 28/29 Feb)
export const addMonths = (d, n) => {
  const x = new Date(d.getTime());
  const day = x.getDate();
  x.setDate(1);
  x.setMonth(x.getMonth() + n);
  x.setDate(Math.min(day, new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate()));
  return x;
};
export const dBetween = (a, b) => Math.round((b - a) / 86400000);
export const daysTo = (s) => {
  const d = pISO(s);
  return d ? dBetween(today(), d) : null;
};
export const monthKey = (s) => String(s || "").slice(0, 7);
export const monthLbl = (k) => {
  const p = String(k).split("-");
  return MONTHS[+p[1] - 1] + " " + String(p[0]).slice(2);
};
export const fmtD = (s) => {
  const d = pISO(s);
  return d ? d.getDate() + " " + MONTHS[d.getMonth()] + " " + String(d.getFullYear()).slice(2) : "—";
};
export const fmtDLong = (s) => {
  const d = pISO(s);
  return d ? d.getDate() + " " + MONTHS[d.getMonth()] + " " + d.getFullYear() : "—";
};
export const dayMonth = (d) => d.getDate() + " " + MONTHS[d.getMonth()];

/* ------------------------------------------------------------ schedule */

export const nextFrom = (d, repeat, every) => {
  const n = Math.max(1, num(every) || 1);
  switch (repeat) {
    case "Daily":
      return addDays(d, n);
    case "Weekly":
      return addDays(d, 7 * n);
    case "Quarterly":
      return addMonths(d, 3 * n);
    case "Yearly":
      return addMonths(d, 12 * n);
    default:
      return addMonths(d, n);
  }
};

export const cycleWord = (p) => {
  const w = UNIT[p.repeat] || "cycle";
  return p.every > 1 ? "every " + p.every + " " + w + "s" : "every " + w;
};
export const cap = (s) => String(s).charAt(0).toUpperCase() + String(s).slice(1);
export const termDays = (terms) => (TERMS.find((t) => t.k === terms) || { days: 30 }).days;

// The first run on or after today (or the start, if that is later), stepping
// from the start date so the day of the month is kept. "" once past the end.
export const firstRunFrom = (start, end, repeat, every) => {
  const t = today();
  let n = pISO(start) || t;
  let guard = 0;
  while (n < t && guard++ < 4000) n = nextFrom(n, repeat, every);
  const out = iso(n);
  return end && out > isoOf(end) ? "" : out;
};

export const upcomingRuns = (next, end, repeat, every, count = 5) => {
  const out = [];
  let cur = pISO(next);
  const stop = end ? isoOf(end) : "";
  for (let i = 0; i < count && cur; i++) {
    if (stop && iso(cur) > stop) break;
    out.push(iso(cur));
    cur = nextFrom(cur, repeat, every);
  }
  return out;
};

/* ------------------------------------------------------------ line maths */

export const blankLine = () => ({
  key: Math.random().toString(36).slice(2),
  guid: "",
  productGuid: "",
  productName: "",
  note: "",
  qty: 1,
  rate: 0,
  disc: 0,
  taxable: true,
  vatPct: 15,
});

export const lineOf = (l) => {
  const qty = num(l.qty);
  const discRate = r2(num(l.rate) * (1 - num(l.disc) / 100));
  const amount = r2(qty * discRate);
  const taxable = l.taxable ? amount : 0;
  const exempt = l.taxable ? 0 : amount;
  const vat = r2((taxable * num(l.vatPct)) / 100);
  return { discRate, taxable, exempt, vat, net: r2(taxable + exempt + vat) };
};

// "Taxable total" is the value before VAT (exempt lines included), as on the mock-up.
// VAT is worked out once per rate, as on every sales screen and in the ZATCA
// e-invoice (core/utils/salesTotals.js; the API's Recompute does the same).
export const totalsOf = (items) => {
  const t = calcBillTotals(
    (items || []).map((l) => {
      const x = lineOf(l);
      const gross = r2(num(l.qty) * num(l.rate));
      return {
        quantity: num(l.qty),
        price: num(l.rate),
        discountAmount: r2(gross - (x.taxable + x.exempt)),
        taxPercent: l.taxable ? num(l.vatPct) : 0,
      };
    })
  );
  return { taxable: t.taxable, vat: t.tax, net: t.net };
};

// one cycle's value expressed per month, so profiles on different cycles add up
export const monthlyValue = (p) => {
  const n = num(p.net);
  const every = Math.max(1, num(p.every) || 1);
  switch (p.repeat) {
    case "Daily":
      return (n * 30) / every;
    case "Weekly":
      return (n * 52) / 12 / every;
    case "Quarterly":
      return n / 3 / every;
    case "Yearly":
      return n / 12 / every;
    default:
      return n / every;
  }
};

/* ------------------------------------------------------------ API → screen */

const ref = (o) => ({ guid: o?.guid || o?.Guid || "", name: o?.name || o?.Name || "" });
const pick = (o, ...keys) => {
  for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
};

export const toLine = (d) => ({
  key: d?.guid || Math.random().toString(36).slice(2),
  guid: d?.guid || "",
  productGuid: ref(d?.product).guid,
  productName: ref(d?.product).name,
  note: d?.note || "",
  qty: num(pick(d, "quantity", "qty")),
  rate: num(d?.rate),
  disc: num(pick(d, "discPercentage", "discount")),
  taxable: pick(d, "isTaxable", "taxable") !== false,
  vatPct: num(pick(d, "taxPercentage", "vatPercentage")),
});

export const toProfile = (r) => {
  const items = (r?.details || r?.items || []).map(toLine);
  const t = totalsOf(items);
  const cust = r?.customer || {};
  return {
    id: r?.id,
    guid: r?.guid || "",
    no: r?.profileNo || "",
    date: isoOf(r?.date),
    custGuid: ref(cust).guid,
    custName: ref(cust).name,
    custEmail: cust.email || "",
    custPhone: cust.mobile || cust.phone || "",
    salesGuid: ref(r?.salesman).guid,
    salesperson: ref(r?.salesman).name,
    whGuid: ref(r?.warehouse).guid,
    warehouse: ref(r?.warehouse).name,
    servGuid: ref(r?.mainProduct).guid,
    service: ref(r?.mainProduct).name,
    type: r?.invoiceType || "",
    repeat: r?.repeatEvery || "Monthly",
    every: Math.max(1, num(r?.repeatInterval) || 1),
    start: isoOf(r?.startDate),
    end: isoOf(r?.endDate),
    next: isoOf(r?.nextInvoiceDate),
    terms: r?.paymentTerms || "Within 30 Days",
    autoEmail: !!r?.autoEmail,
    email: r?.email || "",
    notes: r?.notes || "",
    status: r?.status || "Active",
    failed: String(r?.lastRunStatus || "").toLowerCase() === "failed",
    lastRunMessage: r?.lastRunMessage || "",
    lastRunAt: r?.lastRunAt || "",
    createdBy: r?.createdUserName || "",
    history: Array.isArray(r?.history) ? r.history : null,
    items,
    taxable: t.taxable,
    vat: t.vat,
    net: t.net,
  };
};

export const toInvoice = (r) => {
  const cust = r?.customer || {};
  const items = (r?.details || []).map(toLine);
  return {
    id: r?.id,
    guid: r?.guid || "",
    no: r?.invoiceNo || "",
    profileGuid: ref(r?.profile).guid || r?.profileGuid || "",
    profileNo: r?.profile?.profileNo || r?.profileNo || "",
    custGuid: ref(cust).guid,
    custName: ref(cust).name,
    custEmail: cust.email || "",
    salesGuid: ref(r?.salesman).guid,
    salesperson: ref(r?.salesman).name,
    servGuid: ref(r?.mainProduct).guid,
    service: ref(r?.mainProduct).name,
    type: r?.invoiceType || "",
    terms: r?.paymentTerms || "",
    date: isoOf(r?.invoiceDate),
    due: isoOf(r?.dueDate),
    taxable: num(r?.taxableTotal),
    vat: num(r?.vatTotal),
    net: num(r?.netTotal),
    pay: r?.paymentStatus === "Paid" ? "Paid" : "Pending",
    paidOn: isoOf(r?.paidDate),
    mode: r?.paymentMode || "",
    salesInvoiceNo: r?.salesInvoice?.no || r?.salesInvoiceNo || "",
    receiptNo: r?.receiptNo || "",
    items,
  };
};

export const blankProfile = (defaults = {}) => ({
  guid: "",
  no: defaults.no || "",
  date: iso(today()),
  custGuid: "",
  custName: "",
  custEmail: "",
  custPhone: "",
  salesGuid: "",
  salesperson: "",
  whGuid: defaults.whGuid || "",
  warehouse: "",
  servGuid: "",
  service: "",
  type: "",
  repeat: "Monthly",
  every: 1,
  start: iso(today()),
  end: "",
  next: iso(today()),
  terms: "Within 30 Days",
  autoEmail: false,
  email: "",
  notes: "",
  status: "Active",
  failed: false,
  items: [blankLine()],
});

/* ------------------------------------------------------------ screen → API */

export const toProfilePayload = (d, branch, userId) => {
  const items = (d.items || []).filter((l) => l.productGuid && num(l.qty) > 0);
  const t = totalsOf(items);
  return {
    guid: d.guid || "",
    profileNo: d.no || "",
    date: d.date,
    customer: { guid: d.custGuid },
    salesman: { guid: d.salesGuid },
    warehouse: { guid: d.whGuid },
    mainProduct: { guid: d.servGuid },
    invoiceType: d.type,
    repeatEvery: d.repeat,
    repeatInterval: Math.max(1, num(d.every) || 1),
    startDate: d.start,
    endDate: d.end || null,
    nextInvoiceDate: d.next || null,
    paymentTerms: d.terms,
    autoEmail: !!d.autoEmail,
    email: d.autoEmail ? (d.email || "").trim() : "",
    notes: d.notes || "",
    status: d.status,
    details: items.map((l) => {
      const x = lineOf(l);
      return {
        guid: l.guid || "",
        product: { guid: l.productGuid },
        note: l.note || "",
        quantity: num(l.qty),
        rate: num(l.rate),
        discPercentage: num(l.disc),
        discRate: x.discRate,
        isTaxable: !!l.taxable,
        taxPercentage: l.taxable ? num(l.vatPct) : 0,
        taxAmount: x.vat,
        netAmount: x.net,
      };
    }),
    taxableTotal: t.taxable,
    vatTotal: t.vat,
    netTotal: t.net,
    branch: { guid: branch?.guid },
    updatedUser: `{${userId?.id}}`,
  };
};

export const validateProfile = (d) => {
  const bad = [];
  if (!d.date) bad.push(["date", "Date is required"]);
  if (!d.custGuid) bad.push(["custGuid", "Customer is required"]);
  if (!d.salesGuid) bad.push(["salesGuid", "Salesperson is required"]);
  if (!d.whGuid) bad.push(["whGuid", "Warehouse is required"]);
  if (!d.servGuid) bad.push(["servGuid", "Main service is required"]);
  if (!d.type) bad.push(["type", "Invoice type is required"]);
  if (!d.start) bad.push(["start", "Start date is required"]);
  if (d.end && d.start && d.end < d.start) bad.push(["end", "End date cannot be before the start date"]);
  if (!d.terms) bad.push(["terms", "Payment terms are required"]);
  if (d.autoEmail && !(d.email || "").trim()) bad.push(["email", "An email ID is needed when auto email is on"]);
  else if (d.autoEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim()))
    bad.push(["email", "That email ID does not look right"]);
  if (!(d.items || []).some((l) => l.productGuid && num(l.qty) > 0))
    bad.push(["items", "Add at least one item with a quantity"]);
  return bad;
};

/* ------------------------------------------------------------ figures */

export const counters = (profiles, invoices) => {
  const t = iso(today());
  const mk = monthKey(t);
  const active = profiles.filter((p) => p.status === "Active");
  return {
    active: active.length,
    dueToday: active.filter((p) => p.next === t).length,
    dueWeek: active.filter((p) => {
      const n = daysTo(p.next);
      return n !== null && n >= 0 && n <= 6;
    }).length,
    genMonth: invoices.filter((x) => monthKey(x.date) === mk).length,
    failed: profiles.filter((p) => p.failed && p.status === "Active").length,
    expiring: profiles.filter((p) => p.end && monthKey(p.end) === mk && p.status !== "Cancelled").length,
    cancelled: profiles.filter((p) => p.status === "Cancelled").length,
  };
};

export const monthSeries = (invoices, n) => {
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const k = iso(addMonths(today(), -i)).slice(0, 7);
    const inv = invoices.filter((x) => monthKey(x.date) === k);
    out.push({
      k,
      lbl: monthLbl(k),
      count: inv.length,
      invoiced: r2(inv.reduce((a, x) => a + x.net, 0)),
      collected: r2(inv.filter((x) => x.pay === "Paid").reduce((a, x) => a + x.net, 0)),
    });
  }
  return out;
};

export const sumNet = (rows) => r2(rows.reduce((a, x) => a + num(x.net), 0));
